import "./table.scss";
import Pagination from "../Pagination";
import RadioButton from "../radio-button";
import React, { forwardRef, useEffect, useState } from "react";
import NoDataIcon from "../../../assets/icons/NoDataFound";
import {
  ArrowUp,
  ChevronArrowUp,
  ArrowDown,
  ChevronArrowDown,
  ChevronArrowRight,
} from "../../../assets/icons";
import WidgetLoader from "../widgetloader";
import { useAppSelector } from "../../../store/hooks/StoreHooks";

interface TableColumn {
  key: string;
  render?: any;
  title: string;
  width?: string;
  dataIndex: string;
  sortable?: boolean;
  minwidth?: string;
}

interface TableProps {
  name?: string;
  data: { [key: string]: boolean | number | string | undefined | null | any }[];
  height?: number;
  restrictHeight?: boolean;
  pageNumber?: number;
  rowsPerPage: number;
  totalPages?: number;
  emptyIcon?: boolean;
  tableClass?: string;
  pagination?: boolean;
  errorMessage?: string;
  hiddenPumpId?: string | number | null;
  hiddenMotorId?: string | number | null;
  hiddenVSDId?: string;
  hiddenSwitchBoardId?: string;
  kop?: string | number | null;
  columns: TableColumn[];
  initialSortField?: string;
  initialSortOrder?: string;
  handlePageNumberChange?: (pageNumber: number) => void;
  handleSortChange?: (sortOrder: string, sortBy: string) => void;
  renderExpandedRow?: (rowData: {
    [key: string]: boolean | number | string | undefined | null | any;
  }, rowIndex?: number) => JSX.Element;
  radioSelection?: boolean;
  handleRadioSelection?: (selectedRow: {
    [key: string]: boolean | number | string | undefined | null | any;
  }) => void;
  overFlowClass?: string;
  isLoading?: boolean;
  minimumHeight?: number;
  clickableRows?: boolean; // New prop for making rows clickable
  onRowClick?: (row: any) => void; // New handler for row click event
  isPumpListMode?: boolean;
  singleExpandMode?: boolean; // New prop for single expand mode
  protectorID?: string[];
  showHeader?: boolean;
}

interface SortState {
  [key: string]: "none" | "ASC" | "DESC";
}

const Table = forwardRef<HTMLElement, TableProps>(
  (
    {
      name,
      data = [],
      columns,
      pageNumber,
      pagination,
      tableClass,
      totalPages,
      rowsPerPage,
      height = 500,
      restrictHeight = true,
      errorMessage,
      emptyIcon = true,
      handleSortChange,
      initialSortField,
      initialSortOrder,
      handlePageNumberChange,
      renderExpandedRow,
      radioSelection,
      handleRadioSelection,
      overFlowClass,
      isLoading = false,
      minimumHeight,
      hiddenPumpId,
      hiddenMotorId,
      hiddenVSDId,
      hiddenSwitchBoardId,
      kop,
      clickableRows = false, // Default to false
      onRowClick,
      singleExpandMode = false, // Default to false
      protectorID,
      showHeader = true,
    }, ref) => {

    const currentPage = pageNumber ?? 1;
    const total = totalPages ?? Math.ceil(data.length / (rowsPerPage ?? 1));
    const defaultWidth = `${100 / columns.length}%`;

    const [sortState, setSortState] = useState<SortState>({});
    const [selectedRow, setSelectedRow] = useState<any | null>(null);
    const [isInitialized, setIsInitialized] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
    const [singleExpandedRow, setSingleExpandedRow] = useState<number | null>(null);
    const { customerEnabled , customerCreated} = useAppSelector((state) => state.auth);
    const { projectReadOnly } = useAppSelector((state) => state.projectHeader);

    useEffect(() => {
      if (Object.keys(columns).length > 0 && !isInitialized) {
        const initialSortState = columns.reduce((acc, column) => {
          if (column.sortable) {
            acc[column.dataIndex] = column.dataIndex === initialSortField && initialSortOrder
              ? (initialSortOrder === "ASC" ? "ASC" : "DESC") : "none";
          }
          return acc;
        }, {} as SortState);

        setSortState(initialSortState);
        setIsInitialized(true);

        if (initialSortField && initialSortOrder) {
          handleSortChange?.(initialSortOrder, initialSortField);
        }
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns, initialSortField, initialSortOrder]);

    const toggleSortOrder = (column: string) => {
      setSortState((prevState) => {
        let newOrder: "none" | "ASC" | "DESC" = "none";
        if (prevState[column] === "none") newOrder = "ASC";
        else if (prevState[column] === "ASC") newOrder = "DESC";
        else if (prevState[column] === "DESC") newOrder = "none";

        const updatedState: SortState = {
          ...prevState,
          [column]: newOrder,
        };

        Object.keys(updatedState).forEach((key) => {
          if (key !== column) updatedState[key] = "none";
        });

        handleSortChange && handleSortChange(newOrder, column);

        return updatedState;
      });
    };

    const toggleRowExpansion = (rowIndex: number) => {
      if (singleExpandMode) {
        setSingleExpandedRow((prevIndex) => (prevIndex === rowIndex ? null : rowIndex));
      } else {
        setExpandedRows((prevExpandedRows) => {
          const updatedRows = new Set(prevExpandedRows);
          if (updatedRows.has(rowIndex)) {
            updatedRows.delete(rowIndex);
          } else {
            updatedRows.add(rowIndex);
          }
          return updatedRows;
        });
      }
    };

    const handleRowSelection = (row: any) => {
      setSelectedRow(row);
      handleRadioSelection && handleRadioSelection(row);
    };

    const handleRowClickInternal = (row: any, rowIndex: number) => {
      if (clickableRows && onRowClick) {
        onRowClick(row);
      }
    };

    const renderTableHeader = () => (
      <thead style={{ zIndex: 992 }}>
        <tr>
          {renderExpandedRow && <th></th>}
          {radioSelection && <th></th>}
          {columns.map((column) => (
            <th
              key={column.key}
              className={`px-[24px] py-[16px] text-[12px] cursor-pointer ${column.sortable && sortState[column.dataIndex] !== "none"
                ? "text-primary-400 py-[12px] px-[16px]"
                : "text-neutral-25"
                } hover:bg-darkcolor-500`}
              style={{ width: column.width }}
              onClick={() => column.sortable && toggleSortOrder(column.dataIndex)}
            >
              <div className={`flex items-center ${column.sortable && sortState[column.dataIndex] !== "none"
                ? "table-header-shadow"
                : " "
                }`}>
                <span className={`text-[12px] font-semibold ${column.dataIndex}`}>
                  {column.title}
                </span>
                {column.sortable && (
                  <div className="ml-1 flex flex-col items-center">
                    {sortState[column.dataIndex] === "ASC" && (
                      <ArrowDown
                        color={`${column.sortable &&
                          sortState[column.dataIndex] !== "none"
                          ? "#60BFDA"
                          : "#F7F9F9"
                          }`}
                      />
                    )}
                    {sortState[column.dataIndex] === "DESC" && (
                      <ArrowUp
                        color={`${column.sortable &&
                          sortState[column.dataIndex] !== "none"
                          ? "#60BFDA"
                          : "#F7F9F9"
                          }`}
                      />
                    )}
                    {sortState[column.dataIndex] === "none" && (
                      <>
                        <ChevronArrowUp
                          size={15}
                          className="opacity-50 -mb-[10px]"
                        />
                        <ChevronArrowDown size={15} className="opacity-50" />
                      </>
                    )}
                  </div>
                )}
              </div>
            </th>
          ))}
        </tr>
      </thead>
    );

    const renderTableBody = () => (
      <tbody>
        {isLoading ? (
          <tr>
            <td>
              <div className="absolute inset-0 flex justify-center items-center bg-darkcolor-700 bg-opacity-50">
                <WidgetLoader isLoading={isLoading} />
              </div>
            </td>
          </tr>
        ) : data.length > 0 ? (
          data.map((row, rowIndex) => {
            let isRowSelected =
              ((name === 'pumpList' && hiddenPumpId !== undefined && hiddenPumpId === row?.pumpID) ||
                (name === 'wellSurveyList' && ((hiddenPumpId !== undefined && Number(hiddenPumpId) === Number(row?.md)) || (kop !== undefined && Number(kop) === Number(row?.md)))) ||
                (name === 'motorList' && hiddenMotorId !== undefined && hiddenMotorId === row?.id) ||
                (name === 'VSD' && hiddenVSDId !== undefined && hiddenVSDId === row?.vsD_ID) ||
                (name === 'switchBoard' && hiddenSwitchBoardId !== undefined && hiddenSwitchBoardId === row?.switchBrdID) ||
                (name === 'protectorList') && protectorID !== undefined && protectorID?.includes(row?.protectorID));

            return (
              <React.Fragment key={rowIndex}>
                <tr
                  onClick={() => {
                    if ((!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated && !projectReadOnly)) {
                      if (clickableRows) {
                        handleRowClickInternal(row, rowIndex);
                      }
                      toggleRowExpansion(rowIndex);
                    }
                  }}
                  className={`${clickableRows ? "cursor-pointer" : ""} ${isRowSelected ? "bg-primary-600" : ""
                    }`}
                >
                  {renderExpandedRow && (
                    <td
                      className='px-[24px] py-[9px]'
                      style={{ width: "1%" }}
                    >
                      <ChevronArrowRight
                        size={20}
                        className={`${(!customerEnabled && !projectReadOnly) || (customerEnabled && customerCreated) ? "animate-pulse text-neutral-25 transition-transform duration-300 ease-in-out cursor-pointer" : "cursor-not-allowed opacity-35"} ${singleExpandMode
                          ? singleExpandedRow === rowIndex
                            ? "rotate-90"
                            : ""
                          : expandedRows.has(rowIndex)
                            ? "rotate-90"
                            : ""}`}
                      />
                    </td>
                  )}

                  {radioSelection && (
                    <td
                      className='px-[24px] py-[9px] text-neutral-25 text-[14px]'
                      style={{ width: "1%" }}
                    >
                      <RadioButton
                        checked={selectedRow === row}
                        onChange={() => handleRowSelection(row)}
                      />
                    </td>
                  )}

                  {columns.map((column) => (
                    <td
                      key={column.key}
                      className='px-[24px] py-[9px] text-neutral-25 text-[14px] hover:bg-darkcolor-500'
                      style={{ width: column.width || defaultWidth, minWidth: column?.minwidth }}
                    >
                      {column.render
                        ? column.render(row[column.key], row, rowIndex)
                        : row[column.key]}
                    </td>
                  ))}
                </tr>
                {(singleExpandMode
                  ? singleExpandedRow === rowIndex
                  : expandedRows.has(rowIndex)) &&
                  renderExpandedRow && (
                    <tr>
                      <td
                        colSpan={columns.length + 1 + (radioSelection ? 1 : 0)}
                        className='px-[24px] py-[9px]'
                      >
                        {renderExpandedRow(row, rowIndex)}
                      </td>
                    </tr>
                  )}
              </React.Fragment>
            );
          })
        ) : (
          <tr>
            <td
              colSpan={renderExpandedRow ? columns.length + 1 : columns.length}
              className="py-10"
            >
              <div className="flex flex-col items-center">
                {emptyIcon && (
                  <div className="text-neutral-25 mb-4">
                    <NoDataIcon />
                  </div>
                )}
                <span className="text-neutral-25 title">
                  {errorMessage ?? "No data found"}
                </span>
              </div>
            </td>
          </tr>
        )}
      </tbody>
    );

    const handlePageClick = (pageNumber: number) => {
      setExpandedRows(new Set());
      handlePageNumberChange && handlePageNumberChange(pageNumber);
    };

    return (
      <>
        <div
          className={`scrollable  relative ${overFlowClass}`} ref={ref as any}
          style={{ minHeight: minimumHeight, maxHeight: restrictHeight ? height :"" }}
        >
          <table
            className={`min-w-full rounded-[8px] outline-[1px] table over ${tableClass ? tableClass : ""
              }`}
            style={{ zIndex: 991 }}
          >
            {showHeader && renderTableHeader()}
            {renderTableBody()}
          </table>
        </div>
        {pagination && data.length > 0 && (
          <Pagination
            totalPages={total}
            currentPage={currentPage}
            onPageChange={handlePageClick}
          />
        )}
      </>
    );
  });

export default React.memo(Table);
